import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { Dialog } from "./Overlay";

type AlertOptions = { title?: string };
type ConfirmOptions = { title?: string; confirmLabel?: string; cancelLabel?: string };

type DialogState =
  | { kind: "alert"; message: string; title?: string; resolve: (value: void) => void }
  | { kind: "confirm"; message: string; title?: string; confirmLabel?: string; cancelLabel?: string; resolve: (value: boolean) => void };

type SystemDialogsValue = {
  alert: (message: string, options?: AlertOptions) => Promise<void>;
  confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
};

const SystemDialogsContext = createContext<SystemDialogsValue | null>(null);

/** 轻量提示/确认，底层使用唯一正式 Dialog；高风险确认必须使用 ConfirmDialog。 */
export function SystemDialogsProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const alert = useCallback((message: string, options: AlertOptions = {}) => new Promise<void>((resolve) => {
    setDialog({ kind: "alert", message, ...options, resolve });
  }), []);

  const confirm = useCallback((message: string, options: ConfirmOptions = {}) => new Promise<boolean>((resolve) => {
    setDialog({ kind: "confirm", message, ...options, resolve });
  }), []);

  const close = useCallback(() => setDialog(null), []);

  function confirmDialog() {
    if (!dialog) return;
    const current = dialog;
    if (current.kind === "alert") current.resolve();
    else current.resolve(true);
    close();
  }
  function cancelDialog() {
    if (!dialog) return;
    const current = dialog;
    if (current.kind === "alert") current.resolve();
    else current.resolve(false);
    close();
  }

  useEffect(() => {
    if (!dialog) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") cancelDialog();
      if (event.key === "Enter") confirmDialog();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  return <SystemDialogsContext.Provider value={{ alert, confirm }}>
    {children}
    {dialog && <SystemDialog dialog={dialog} onConfirm={confirmDialog} onCancel={cancelDialog} />}
  </SystemDialogsContext.Provider>;
}

function SystemDialog({ dialog, onConfirm, onCancel }: { dialog: DialogState; onConfirm: () => void; onCancel: () => void }) {
  const title = dialog.title ?? (dialog.kind === "alert" ? "提示" : "确认操作");
  const cancelLabel = dialog.kind === "alert" ? undefined : dialog.cancelLabel ?? "取消";
  const confirmLabel = dialog.kind === "alert" ? "确定" : dialog.confirmLabel ?? "确认";
  return <Dialog title={title} description={dialog.message} onClose={onCancel}>
    <div className="form-grid">
      <div className="form-actions full">
        {dialog.kind !== "alert" && <button type="button" className="button secondary" onClick={onCancel}>{cancelLabel}</button>}
        <button type="button" className="button primary" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  </Dialog>;
}

export function useSystemDialogs(): SystemDialogsValue {
  const value = useContext(SystemDialogsContext);
  if (!value) throw new Error("useSystemDialogs 必须在 SystemDialogsProvider 内使用");
  return value;
}
